import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useMenu } from '@/hooks/useMenu';
import { findNodeInTree } from '@/pages/system/ops-center/service-catalog/utils';

import navStyles from '../styles/index.module.less';
import { DropdownButton } from './components/DropdownButton';
import { TutorialsDropdown } from './components/TutorialsDropdown';
import menuStyles from './styles/index.module.less';

/** 动态菜单：一级导航共享一个常驻浮层，横向切换时只替换内容。 */
export default function Index({ menus = undefined }) {
  const fetchedMenu = useMenu();
  const menu = menus || fetchedMenu;
  const { pathname, search } = useLocation();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout>>();
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();

  const listData = useMemo(() => {
    if (!Array.isArray(menu)) return [];
    return menu.filter((item) => !!item.children?.length || !!item.path);
  }, [menu]);

  // SKU 列表通过 productCatalogueItemId 定位产品目录叶子，并保持所属一级导航高亮。
  const selectedIndex = useMemo(() => {
    if (pathname !== '/product/all-skus') return -1;
    const params = new URLSearchParams(search);
    const catalogueItemId = params.get('productCatalogueItemId') || params.get('productItemId');
    if (!catalogueItemId) return -1;
    return listData.findIndex((item) => !!findNodeInTree([item], catalogueItemId));
  }, [listData, pathname, search]);

  const clearTimers = () => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
  };

  useEffect(() => clearTimers, []);

  const openMenu = (index: number | null) => {
    clearTimers();
    if (index === activeIndex) return;
    if (index === null) {
      setActiveIndex(index);
      return;
    }
    // 已展开时增加少量 hover intent，避免鼠标快速掠过相邻入口造成误切换。
    openTimer.current = setTimeout(() => setActiveIndex(index), activeIndex === null ? 120 : 160);
  };

  const closeMenu = () => {
    clearTimers();
    closeTimer.current = setTimeout(() => setActiveIndex(null), 220);
  };

  const activeMenu = activeIndex === null ? null : listData[activeIndex];

  return (
    <div className={navStyles.navMenu} onMouseEnter={() => clearTimeout(closeTimer.current)} onMouseLeave={closeMenu}>
      {listData.map((item: any, index) => (
        <DropdownButton
          active={activeIndex === index || (activeIndex === null && selectedIndex === index)}
          index={index}
          key={item.id || item.title || index}
          menu={item}
          onEnter={openMenu}
        />
      ))}

      {activeMenu?.children?.length > 0 && (
        <div className={menuStyles.dropdownPanel} onMouseEnter={() => clearTimeout(closeTimer.current)} onMouseLeave={closeMenu}>
          <TutorialsDropdown
            desc={activeMenu.desc}
            lists={activeMenu.children}
            onClose={() => setActiveIndex(null)}
            title={activeMenu.title}
          />
        </div>
      )}
    </div>
  );
}
